/**
 * The composer's multi-photo staging area (R1, R2): up to 10 files, each
 * uploaded independently through the same `/upload/init` -> presigned PUT ->
 * `/upload/complete` sequence `useChatPhotoUpload.ts` already drives for a
 * chat's own photo, purpose `chatPhoto` (there is no separate "message
 * photo" purpose). Selection is validated first by the pure
 * `validatePhotoSelection` so the network dance below only ever runs for a
 * file that already passed the count/type/size gate.
 *
 * Every attachment carries a client-only `localId` (never the server's
 * `mediaId`, which does not exist until `/upload/init` returns) so a fan can
 * remove or retry one attachment among several in-flight uploads without the
 * others shifting under it.
 */
import { useUploadComplete, useUploadInit } from "@sportsmet/media-sdk/private";
import { useCallback, useRef, useState } from "react";

import { COMPOSER_MAX_PHOTOS } from "./chatExtrasCopy";
import { validatePhotoSelection } from "./composerPhotoLogic";
import type { typeChatPhotoContentType } from "./useChatPhotoUpload";

export type typeComposerPhotoAttachment = {
  localId: string;
  file: File;
  previewUrl: string;
  state: "uploading" | "ready" | "failed";
  mediaId: string | null;
};

export function useComposerPhotoUpload(): {
  attachments: typeComposerPhotoAttachment[];
  /** True when at least one incoming file was refused by the count cap. */
  hasTooManyRejection: boolean;
  addFiles: (files: File[]) => void;
  removeAttachment: (localId: string) => void;
  retryAttachment: (localId: string) => void;
  reset: () => void;
} {
  const uploadInit = useUploadInit();
  const uploadComplete = useUploadComplete();
  const [attachments, setAttachments] = useState<typeComposerPhotoAttachment[]>(
    [],
  );
  const [hasTooManyRejection, setHasTooManyRejection] = useState(false);
  // Attachments are read inside `run`, which closures capture at call time;
  // a ref keeps the count check honest across overlapping `addFiles` calls
  // in the same tick, the same reason `useChatPhotoUpload.ts` keeps its own
  // re-entrancy guard on a ref rather than on state.
  const attachmentsRef = useRef<typeComposerPhotoAttachment[]>([]);

  const setAndTrack = useCallback(
    (
      updater: (
        prev: typeComposerPhotoAttachment[],
      ) => typeComposerPhotoAttachment[],
    ) => {
      setAttachments((prev) => {
        const next = updater(prev);
        attachmentsRef.current = next;
        return next;
      });
    },
    [],
  );

  const runUpload = useCallback(
    (localId: string, file: File): void => {
      async function run(): Promise<void> {
        const requestId = crypto.randomUUID();

        let init;
        try {
          init = await uploadInit.mutateAsync({
            purpose: "chatPhoto",
            contentType: file.type as typeChatPhotoContentType,
            byteSize: file.size,
          });
        } catch {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }
        if (init.error != null || init.data == null) {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }

        let putResponse: Response;
        try {
          putResponse = await fetch(init.data.uploadUrl, {
            method: init.data.uploadMethod,
            headers: { "content-type": init.data.uploadHeaders.contentType },
            body: file,
          });
        } catch {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }
        if (!putResponse.ok) {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }

        let complete;
        try {
          complete = await uploadComplete.mutateAsync({
            mediaId: init.data.mediaId,
            requestId,
          });
        } catch {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }
        if (complete.error != null || complete.data == null) {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }

        // A photo the moderation pipeline is still checking is not ready to
        // send (its `mediaId` is not yet a valid `mediaObjectIds` entry per
        // `services/messaging-api`'s `validateMediaObjectIds`), so it is
        // treated the same as a failure here: the fan removes or waits, but
        // this composer never sends an unresolved attachment.
        if (
          complete.data.status === "pendingReview" ||
          complete.data.status === "heldOnBudget"
        ) {
          setAndTrack((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, state: "failed" } : a,
            ),
          );
          return;
        }

        const readyMediaId = complete.data.mediaId;
        setAndTrack((prev) =>
          prev.map((a) =>
            a.localId === localId
              ? { ...a, state: "ready", mediaId: readyMediaId }
              : a,
          ),
        );
      }

      void run();
    },
    [uploadInit, uploadComplete, setAndTrack],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      const { accepted, rejected } = validatePhotoSelection({
        currentCount: attachmentsRef.current.length,
        incomingFiles: files,
        maxPhotos: COMPOSER_MAX_PHOTOS,
      });
      setHasTooManyRejection(rejected.some((r) => r.reason === "tooMany"));

      const newAttachments: typeComposerPhotoAttachment[] = accepted.map(
        (file) => ({
          localId: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          state: "uploading" as const,
          mediaId: null,
        }),
      );
      if (newAttachments.length === 0) return;

      setAndTrack((prev) => [...prev, ...newAttachments]);
      for (const attachment of newAttachments) {
        runUpload(attachment.localId, attachment.file);
      }
    },
    [runUpload, setAndTrack],
  );

  const removeAttachment = useCallback(
    (localId: string) => {
      setAndTrack((prev) => {
        const found = prev.find((a) => a.localId === localId);
        if (found) URL.revokeObjectURL(found.previewUrl);
        return prev.filter((a) => a.localId !== localId);
      });
    },
    [setAndTrack],
  );

  const retryAttachment = useCallback(
    (localId: string) => {
      const target = attachmentsRef.current.find((a) => a.localId === localId);
      if (!target) return;
      setAndTrack((prev) =>
        prev.map((a) =>
          a.localId === localId ? { ...a, state: "uploading" } : a,
        ),
      );
      runUpload(localId, target.file);
    },
    [runUpload, setAndTrack],
  );

  const reset = useCallback(() => {
    for (const attachment of attachmentsRef.current) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
    setHasTooManyRejection(false);
    setAndTrack(() => []);
  }, [setAndTrack]);

  return {
    attachments,
    hasTooManyRejection,
    addFiles,
    removeAttachment,
    retryAttachment,
    reset,
  };
}
